import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiCopy, FiTrash2, FiLoader, FiImage, FiRefreshCw, FiSearch, FiAlertCircle } = FiIcons;

const PhotoGalleryModal = ({ isOpen, onClose, onPhotoSelect, selectedPhoto = null }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // 🔥 AKTIVIERTE Load photos Funktion
  const loadPhotos = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('menu_photos_a7b9c8d6e5')
        .select('*')
        .eq('is_active', true)
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.log('Photo table error:', error);
        setPhotos([]);
        setError('Fehler beim Laden der Fotos.');
        return;
      }

      setPhotos(data || []);
    } catch (err) {
      console.log('Error loading photos:', err);
      setPhotos([]);
      setError('Foto-Galerie konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  // Load photos when modal opens
  useEffect(() => {
    if (isOpen) {
      loadPhotos();
    }
  }, [isOpen]);

  // Filter photos based on search term
  const filteredPhotos = photos.filter(photo =>
    photo.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (photo.description && photo.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Copy URL to clipboard
  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('URL kopiert!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Fehler beim Kopieren der URL');
    }
  };

  // Delete photo
  const deletePhoto = async (photo) => {
    if (!confirm(`Foto "${photo.original_filename}" wirklich löschen?`)) return;
    
    try {
      // Lösche aus Storage
      const { error: storageError } = await supabase.storage
        .from('menu-photos')
        .remove([photo.file_path]);

      if (storageError) {
        console.error('Storage delete error:', storageError);
      }

      // Lösche aus Datenbank
      const { error: dbError } = await supabase
        .from('menu_photos_a7b9c8d6e5')
        .delete()
        .eq('id', photo.id);

      if (dbError) {
        throw new Error(`Fehler beim Löschen: ${dbError.message}`);
      }

      setSuccess('Foto erfolgreich gelöscht!');
      await loadPhotos();
      setTimeout(() => setSuccess(''), 2000);

    } catch (err) {
      setError(err.message || 'Fehler beim Löschen');
    }
  };

  // Select photo and close modal
  const selectPhoto = (url) => {
    onPhotoSelect(url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiImage} className="mr-2 text-primary-600" />
            Foto-Galerie ({filteredPhotos.length})
          </h2>
          <div className="flex items-center space-x-3">
            <motion.button
              onClick={loadPhotos}
              disabled={loading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={loading ? 'animate-spin' : ''} />
            </motion.button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiX} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Fotos durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center">
            <SafeIcon icon={FiAlertCircle} className="mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {/* Photo Grid */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <SafeIcon icon={FiLoader} className="animate-spin text-3xl text-primary-600 mr-3" />
              <span className="text-gray-600">Fotos werden geladen...</span>
            </div>
          ) : filteredPhotos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredPhotos.map((photo) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`relative group rounded-xl overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                    selectedPhoto === photo.public_url
                      ? 'border-primary-500 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                  onClick={() => selectPhoto(photo.public_url)}
                >
                  <div className="aspect-square">
                    <img
                      src={photo.public_url}
                      alt={photo.original_filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/300x300?text=Bild+nicht+verfügbar';
                      }}
                    />
                  </div>

                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectPhoto(photo.public_url);
                        }}
                        className="p-2 bg-white text-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                        title="Foto auswählen"
                      >
                        <SafeIcon icon={FiCheck} />
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(photo.public_url);
                        }}
                        className="p-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                        title="URL kopieren"
                      >
                        <SafeIcon icon={FiCopy} />
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          deletePhoto(photo);
                        }}
                        className="p-2 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        title="Foto löschen"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </motion.button>
                    </div>
                  </div>

                  {/* Selection indicator */}
                  {selectedPhoto === photo.public_url && (
                    <div className="absolute top-2 right-2 bg-primary-500 text-white p-1 rounded-full">
                      <SafeIcon icon={FiCheck} className="text-xs" />
                    </div>
                  )}

                  {/* File info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                    <p className="text-white text-xs truncate">{photo.original_filename}</p>
                    <p className="text-white text-xs opacity-75">
                      {(photo.file_size / 1024 / 1024).toFixed(1)} MB
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <SafeIcon icon={FiImage} className="mx-auto text-6xl mb-4 opacity-50" />
              {searchTerm ? (
                <>
                  <p className="text-lg mb-2">Keine Fotos gefunden</p>
                  <p className="text-sm">Versuchen Sie andere Suchbegriffe</p>
                </>
              ) : (
                <>
                  <p className="text-lg mb-2">Noch keine Fotos vorhanden</p>
                  <p className="text-sm">Laden Sie Ihr erstes Foto hoch!</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Klicken Sie auf ein Foto, um es auszuwählen
            </p>
            <motion.button
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-xl font-medium hover:bg-gray-300 transition-colors"
            >
              Schließen
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PhotoGalleryModal;